import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { GOLD, GOLD_LT, CREAM, INK } from '../theme';

const ConfirmContext = createContext(null);

// Promise-based replacement for window.confirm() — same calling convention
// (await confirm('message')), but renders an on-brand modal instead of a
// native browser dialog (which blocks all page interaction, including
// automated testing, until manually dismissed).
export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialog({
        message,
        danger: opts.danger !== false,
        confirmLabel: opts.confirmLabel || 'Delete',
        cancelLabel: opts.cancelLabel || 'Cancel',
      });
    });
  }, []);

  const settle = (result) => {
    setDialog(null);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div
          role="presentation"
          onClick={() => settle(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,7,4,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ background: INK, border: `1px solid ${GOLD}`, padding: 'clamp(28px,4vw,40px)', maxWidth: 420, width: '100%', fontFamily: "'Cormorant Garamond', serif" }}
          >
            <div style={{ fontSize: 18, lineHeight: 1.6, color: CREAM, margin: '0 0 28px', whiteSpace: 'pre-line' }}>{dialog.message}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => settle(false)}
                style={{ fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, background: 'none', border: `1px solid ${GOLD_LT}`, padding: '11px 22px', cursor: 'pointer' }}
              >
                {dialog.cancelLabel}
              </button>
              <button
                onClick={() => settle(true)}
                style={{ fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: INK, background: dialog.danger ? '#e08a8a' : GOLD, border: 'none', padding: '11px 22px', cursor: 'pointer' }}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
